import { Crown } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/zustand/Zustand';
import UpgradeToPremiumButton from './UpgradeToPremiumButton';

// Dismissed per browser session only (sessionStorage), so the banner returns on
// the next session. Intentionally NOT persisted via seenTours: it is a countdown
// banner, not a one-time announcement.
const DISMISS_KEY = 'fp-premium-preview-banner-dismissed';

const readDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const PremiumPreviewBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const { userData } = useAuth();
  const [dismissed, setDismissed] = useState(readDismissed);
  // Captured once on mount: the remaining-days figure only needs day-level
  // precision, and render purity forbids calling Date.now() inline.
  const [mountedAt] = useState(() => Date.now());
  // The server only recomputes inPremiumPreview when userData is refetched, so
  // cached userData in a long-lived session would keep the banner up after the
  // window ends — flip it off client-side the moment the expiry passes.
  const [expired, setExpired] = useState(false);

  const expiresAtMs = userData?.premiumPreviewExpiresAt
    ? new Date(userData.premiumPreviewExpiresAt).getTime()
    : null;

  useEffect(() => {
    if (expiresAtMs === null) return;
    const remaining = expiresAtMs - Date.now();
    if (remaining <= 0) return; // already hidden by the render guard below
    // Clamp to the 32-bit setTimeout ceiling — an overflowing delay fires
    // immediately, which would wrongly hide an active banner.
    const id = setTimeout(() => setExpired(true), Math.min(remaining, 2 ** 31 - 1));
    return () => clearTimeout(id);
  }, [expiresAtMs]);

  if (!userData?.inPremiumPreview || dismissed || expired) return null;
  // Stale cached userData: window already over at mount.
  if (expiresAtMs !== null && expiresAtMs <= mountedAt) return null;

  const daysLeft = expiresAtMs
    ? Math.max(0, Math.ceil((expiresAtMs - mountedAt) / 86400000))
    : 0;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // sessionStorage unavailable — dismiss for this render only
    }
    setDismissed(true);
  };

  return (
    <div
      data-tour="premium-preview-banner"
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex flex-wrap items-center gap-3 rounded-lg bg-fp-card border border-orange-500/40 px-4 py-3 shadow-xl text-sm text-gray-200 max-w-xl w-[calc(100%-2rem)] sm:w-auto"
    >
      <Crown className="w-4 h-4 text-orange-400 shrink-0" />
      <span className="text-orange-300 font-medium">
        {t('premiumPreviewBanner.title')}
      </span>
      <span className="text-gray-400">
        {daysLeft === 1
          ? t('premiumPreviewBanner.dayLeftOne')
          : t('premiumPreviewBanner.daysLeft', { count: daysLeft })}
      </span>
      <UpgradeToPremiumButton
        variant="compact"
        className="ml-auto shrink-0 px-3! py-1! text-xs!"
      />
      <button
        onClick={handleDismiss}
        aria-label={t('premiumPreviewBanner.dismiss')}
        className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
      >
        ✕
      </button>
    </div>
  );
};

export default PremiumPreviewBanner;
